import { mixed, object, ObjectSchema, string } from 'yup';

import { BasicContactWithIdDto, CustomerPropertiesDto } from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { selectSearchOptionRequiredValidation } from '@/utils/form/selectSearchOptionOptional.validation.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import {
  processCalendarEntryQueryShape,
  useProcessCalendarEntryQueryMessages,
} from '@/views/processes/calendarEntryQuery/util/processCalendarEntryQuery.validation.ts';
import {
  CreateProcessFormModel,
  CreateProcessFormValidationContext,
} from '@/views/processesCreate/model/CreateProcessForm.model.ts';

export const useCreateProcessFormValidation = (): ObjectSchema<CreateProcessFormModel> => {
  const { t } = useTranslationWithPrefix('validation');
  const calendarEntryQueryMessages = useProcessCalendarEntryQueryMessages();
  // the offer fields are required only while the offer section is ready, which the dialog passes as context
  const offerField = () =>
    mixed<SelectSearchOptionModel<number>>()
      .nullable()
      .default(null)
      .test(
        'offer-required',
        t('general.required'),
        (value, context) =>
          Boolean(value) ||
          !(context.options.context as CreateProcessFormValidationContext | undefined)
            ?.isOfferRequired,
      );

  return object().shape({
    ...processCalendarEntryQueryShape(calendarEntryQueryMessages),
    contact: mixed<Partial<BasicContactWithIdDto>>().nullable(),
    customer: mixed<Partial<CustomerPropertiesDto>>().nullable(),
    description: string().optional(),
    emailThreadId: string().optional(),
    name: string()
      .min(3, t('minLength', { min: 3 }))
      .required(t('general.required')),
    noteText: string().optional(),
    offerBookingTarget: object({ definition: offerField(), item: offerField() }),
    processOwnerId: selectSearchOptionRequiredValidation<string>(string(), t),
  });
};
